import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { lowercaseFirstLetter } from '~/utils/helpers';

import { visitIQAudience } from '../constants';
import { useSessionContext } from '../contexts/SessionContext';
import {
  AudienceOverviewQuery,
  FieldCategory,
  FieldType,
  FilterStringField,
  useAudienceOverviewQuery,
  usePeopleFieldsQuery,
} from '../types/api.graphql';

type Nullable<T> = T | null;

const propertiesBlackList = [
  FilterStringField.Email,
  FilterStringField.FirstName,
  FilterStringField.LastName,
  FilterStringField.PathPrefix,
  FilterStringField.Phone,
  FilterStringField.PulseTopic,
  FilterStringField.UtmCampaign,
  FilterStringField.UtmMedium,
  FilterStringField.UtmSource,
  FilterStringField.UtmSourceAndMedium,
];

export const propertiesB2B = [
  FilterStringField.CompanyName,
  FilterStringField.CompanyRevenue,
  FilterStringField.Department,
  FilterStringField.JobTitle,
  FilterStringField.PrimaryIndustry,
  FilterStringField.SeniorityLevel,
  FilterStringField.CompanyCity,
  FilterStringField.CompanyDomain,
  FilterStringField.CompanySic,
  FilterStringField.CompanyState,
  FilterStringField.CompanyEmployeeCount,
];

export const propertiesMapAvailable = [FilterStringField.PostalCode, FilterStringField.State];

export enum TaxonomyChartType {
  column = 'column',
  bar = 'bar',
  donut = 'donut',
  line = 'line',
  funnel = 'funnel',
  map = 'map',
}

export interface TaxonomyProperty {
  key: string;
  type: FieldType;
}

enum SettingsGroup {
  Taxonomy = 'taxonomy',
  Journey = 'journey',
  Attributions = 'attributions',
  BusinessInsights = 'businessInsights',
}

const settingsChartGroups = [SettingsGroup.Taxonomy, SettingsGroup.Journey, SettingsGroup.BusinessInsights];

type SettingsType = 'audience' | 'context';

type PickSettings<AudienceType, ContextType, TypeKey extends SettingsType> = TypeKey extends 'audience'
  ? AudienceType
  : ContextType;

type SettingsFactory<TypeKey extends SettingsType> = {
  [SettingsGroup.Taxonomy]?: PickSettings<TaxomonySettings, TaxonomyContextProps, TypeKey>;
  [SettingsGroup.Journey]?: PickSettings<ChartsSettings, ChartContextProps, TypeKey>;
  [SettingsGroup.Attributions]?: PickSettings<AttributionSourcesSettings, AttributionSourcesContextProps, TypeKey>;
  [SettingsGroup.BusinessInsights]?: PickSettings<BusinessInsightsSettings, BusinessInsightsContextProps, TypeKey>;
};

enum ChartSettingsProperty {
  Properties = 'properties',
  Charts = 'charts',
}

interface ChartsSettings {
  [ChartSettingsProperty.Properties]: TaxonomyProperty[];
  [ChartSettingsProperty.Charts]: TaxonomyChartType[];
}

interface TaxomonySettings extends ChartsSettings {
  selectedLayout?: number;
  customChartCount?: number;
  selectedTab?: string;
}

interface AttributionSourcesSettings {
  selectedGroup: string;
}

interface BusinessInsightsSettings extends ChartsSettings {
  selectedCategory: string;
}

export type AudienceSettings = SettingsFactory<'audience'>;

interface ChartContextProps {
  selectedChartProperty?: (index: number) => Nullable<TaxonomyProperty>;
  setSelectedChartProperty?: (index: number, property: TaxonomyProperty) => void;
  selectedChartType?: (index: number) => Nullable<TaxonomyChartType>;
  setSelectedChartType?: (index: number, property: TaxonomyChartType) => void;
}

interface TaxonomyContextProps extends ChartContextProps {
  selectedLayout?: () => Nullable<number>;
  setSelectedLayout?: (selectedLayout: number) => void;
  addCustomChart?: () => void;
  removeCustomChart?: (chartIndex: number) => void;
  getCustomChartCount?: () => number;
}

interface AttributionSourcesContextProps {
  selectedGroup?: () => Nullable<string>;
  setSelectedGroup?: (group: string) => void;
}

interface BusinessInsightsContextProps extends ChartContextProps {
  selectedCategory?: () => Nullable<string>;
  setSelectedCategory?: (category: string) => void;
}

type AudienceContextSettingsProps = SettingsFactory<'context'>;

interface AudienceContextBaseProps {
  audienceId: Nullable<string>;
  setAudienceId: (audienceId: string) => void;
  isLoading: boolean;
  audienceData: Nullable<AudienceOverviewQuery>;
  taxonomyPropertiesByCategory: (category: FieldCategory) => TaxonomyProperty[];
  hiddenPeopleFields?: string[];
}

type AudienceContextProps = AudienceContextBaseProps & AudienceContextSettingsProps;
interface AudienceProviderProps {
  children: ReactNode;
}

const AudienceContext = createContext<AudienceContextProps>({
  audienceId: null,
  setAudienceId: undefined,
  isLoading: false,
  audienceData: undefined,
  taxonomyPropertiesByCategory: undefined,
  hiddenPeopleFields: undefined,
});

export const AudienceProvider: FC<AudienceProviderProps> = ({ children }) => {
  const { me, getHiddenPeopleFieldsForOrg } = useSessionContext();
  const [audienceId, setAudienceId] = useState<Nullable<string>>(null);
  const [settings, setSettings] = useState<AudienceSettings>({});

  const { data: audienceData, loading } = useAudienceOverviewQuery({
    skip: !audienceId,
    variables: {
      audienceId,
    },
  });

  const hiddenPeopleFields = useMemo(() => {
    if (!audienceData?.audience?.organization) return undefined;

    return getHiddenPeopleFieldsForOrg(audienceData?.audience?.organization);
  }, [audienceData]);

  const { data: peopleFieldsData } = usePeopleFieldsQuery();

  const audienceCondition = audienceData?.audience?.condition;

  const personProperties: TaxonomyProperty[] = useMemo(() => {
    if (!peopleFieldsData?.peopleFields) return [];

    return peopleFieldsData.peopleFields
      .filter((field) => {
        if (!field) return false;
        if (hiddenPeopleFields?.includes(lowercaseFirstLetter(field.filterField))) return false;
        // TODO: Replace propertiesBlackList with an API-driven approach (e.g. a `useTaxonomy: true`
        // filter parameter or an `isTaxonomyEnabled` field on peopleFields) so the backend controls
        // what's shown in taxonomy without requiring frontend enum updates.
        if (propertiesBlackList.includes(field.filterField as unknown as FilterStringField)) return false;
        // Exclude boolean fields (e.g. AmexCard) and extended string fields (e.g. PixelHitUrl)
        // since they are not supported in the audience taxonomy filter UI.
        if (![FieldType.String, FieldType.Number].includes(field.type)) return false;
        if (field.configuration?.stringFilterConfiguration?.isExtendedStringFilter) return false;
        if (
          field.configuration?.fieldAudienceTypes?.length &&
          audienceCondition &&
          !field.configuration.fieldAudienceTypes.includes(audienceCondition)
        ) {
          return false;
        }
        return true;
      })
      .map((field) => ({
        key: field.filterField,
        type: field.type,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [peopleFieldsData, hiddenPeopleFields, audienceCondition]);

  const taxonomyPropertiesByCategory = useCallback(
    (category: FieldCategory) =>
      category === FieldCategory.Person
        ? personProperties
        : propertiesB2B.map((key) => ({ key, type: FieldType.String })),
    [personProperties],
  );

  const updateSettings = useCallback(
    (newSettings: AudienceSettings) => {
      setSettings(newSettings);
      localStorage.setItem(`${visitIQAudience}-${me.id}-${audienceId}`, JSON.stringify(newSettings));
    },
    [me?.id, audienceId],
  );

  const createEmptyGroupSettingsIfNeeded = (group: SettingsGroup) => {
    if (!settings[group]) {
      settings[group.toString()] = {};
    }
  };

  const setSettingsValue = (group: SettingsGroup, property: string, value: any) => {
    createEmptyGroupSettingsIfNeeded(group);
    settings[group][property] = value;
    updateSettings(settings);
  };

  const chartsSettingsContextValues: AudienceContextSettingsProps = useMemo(() => {
    const createEmptyArrayFieldIfNeeded = (group: SettingsGroup, arrayKey: ChartSettingsProperty) => {
      createEmptyGroupSettingsIfNeeded(group);
      if (!settings[group][arrayKey]) {
        settings[group][arrayKey] = [];
      }
    };

    const chartsSettings: AudienceContextSettingsProps = {};
    settingsChartGroups.forEach((group) => {
      chartsSettings[group] = {
        selectedChartProperty: (index: number) => {
          return settings?.[group]?.[ChartSettingsProperty.Properties]?.[index];
        },
        setSelectedChartProperty: (index: number, property: TaxonomyProperty) => {
          createEmptyArrayFieldIfNeeded(group, ChartSettingsProperty.Properties);
          settings[group][ChartSettingsProperty.Properties][index] = property;
          updateSettings(settings);
        },
        selectedChartType: (index: number) => {
          return settings?.[group]?.[ChartSettingsProperty.Charts]?.[index];
        },
        setSelectedChartType: (index: number, type: TaxonomyChartType) => {
          createEmptyArrayFieldIfNeeded(group, ChartSettingsProperty.Charts);
          settings[group][ChartSettingsProperty.Charts][index] = type;
          updateSettings(settings);
        },
      };
    });
    return chartsSettings;
  }, [settings, updateSettings]);

  useEffect(() => {
    if (!me || !audienceId) return;

    let settings = {};
    const storageItem = localStorage.getItem(`${visitIQAudience}-${me.id}-${audienceId}`);
    if (storageItem) {
      settings = JSON.parse(storageItem) as AudienceSettings;
    }

    setSettings(settings);
  }, [me, audienceId]);

  const contextValue: AudienceContextProps = useMemo(
    () => ({
      audienceId,
      setAudienceId,
      isLoading: loading || !audienceId,
      audienceData,
      hiddenPeopleFields,
      taxonomyPropertiesByCategory,
      ...chartsSettingsContextValues,
      taxonomy: {
        ...chartsSettingsContextValues.taxonomy,
        selectedLayout: () => settings.taxonomy?.selectedLayout || 0,
        setSelectedLayout: (selectedLayout: number) =>
          setSettingsValue(SettingsGroup.Taxonomy, 'selectedLayout', selectedLayout),
        addCustomChart: () => {
          const currentCount = settings.taxonomy?.customChartCount || 0;
          setSettingsValue(SettingsGroup.Taxonomy, 'customChartCount', currentCount + 1);
        },
        removeCustomChart: (chartIndex: number) => {
          const currentCount = settings.taxonomy?.customChartCount || 0;
          if (currentCount > 0 && chartIndex >= 0) {
            const properties = settings.taxonomy?.properties ? [...settings.taxonomy.properties] : [];
            const charts = settings.taxonomy?.charts ? [...settings.taxonomy.charts] : [];
            if (chartIndex < properties.length) properties.splice(chartIndex, 1);
            if (chartIndex < charts.length) charts.splice(chartIndex, 1);

            updateSettings({
              ...settings,
              taxonomy: {
                ...settings.taxonomy,
                properties,
                charts,
                customChartCount: Math.max(0, currentCount - 1),
              },
            });
          }
        },
        getCustomChartCount: () => settings.taxonomy?.customChartCount || 0,
      },
      attributions: {
        selectedGroup: () => settings.attributions?.selectedGroup,
        setSelectedGroup: (selectedGroup: string) =>
          setSettingsValue(SettingsGroup.Attributions, 'selectedGroup', selectedGroup),
      },
      businessInsights: {
        ...chartsSettingsContextValues.businessInsights,
        selectedCategory: () => settings.businessInsights?.selectedCategory,
        setSelectedCategory: (selectedCategory: string) =>
          setSettingsValue(SettingsGroup.BusinessInsights, 'selectedCategory', selectedCategory),
      },
    }),
    [
      audienceId,
      loading,
      audienceData,
      hiddenPeopleFields,
      taxonomyPropertiesByCategory,
      chartsSettingsContextValues,
      settings,
    ],
  );

  return <AudienceContext.Provider value={contextValue}>{children}</AudienceContext.Provider>;
};

export const useAudienceContext: () => AudienceContextProps = () => {
  return React.useContext(AudienceContext);
};
